import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Save, ChevronDown, ChevronRight, ChevronUp, Info, Shield, FileText } from 'lucide-react';
import { CONTROL_TYPES, MATURITY_LEVELS } from '../../constants/assessmentTypes';
import { questions } from '../../data/questions';
import { methodologyService } from '../../services/methodologyService';
import { useAuth } from '../../contexts/AuthContext';
import strategyInfoData from '../../data/strategyInfo.json';
import StrategyInfoModal from './StrategyInfoModal';
import MaturityLevelsPanel from './MaturityLevelsPanel';
import StrategySidebar from './StrategySidebar';
import SidebarOverlay from './SidebarOverlay';

// Control strategies grouping
const CONTROL_STRATEGIES = {
  PREVENT_MALWARE: {
    title: "🛡️ Preventing Malware Execution",
    description: "Strategies that aim to stop malicious code from being executed on systems",
    controls: ["APP_CONTROL", "OFFICE_MACROS", "APP_HARDENING"]
  },
  LIMIT_INCIDENTS: {
    title: "🔐 Limiting the Extent of Cyber Security Incidents",
    description: "Controls that reduce the ability for attackers to move laterally or escalate privileges once initial access is gained",
    controls: ["ADMIN_PRIVILEGES", "MFA", "PATCH_APPLICATIONS", "PATCH_OS"]
  },
  RECOVERY: {
    title: "💾 Recovering Data and System Availability",
    description: "Focused on business continuity and resilience post-incident",
    controls: ["BACKUPS"]
  }
};

// Helper function to get the strategy for a control
const getControlStrategy = (controlId) => {
  for (const [strategyKey, strategy] of Object.entries(CONTROL_STRATEGIES)) {
    if (strategy.controls.includes(controlId)) {
      return strategyKey;
    }
  }
  return null;
};

// Add maturity level descriptions
const MATURITY_LEVEL_INFO = {
  [MATURITY_LEVELS.ML1]: {
    title: "Maturity Level One",
    description: `The focus of this maturity level is malicious actors who are content to simply leverage commodity tradecraft that is widely available in order to gain access to, and likely control of, a system. For example, malicious actors opportunistically using a publicly-available exploit for a vulnerability in an online service which has not been patched, or authenticating to an online service using credentials that were stolen, reused, brute forced or guessed.

Generally, malicious actors are looking for any victim rather than a specific victim and will opportunistically seek common weaknesses in many targets rather than investing heavily in gaining access to a specific target. Malicious actors will employ common social engineering techniques to trick users into weakening the security of a system and launch malicious applications. If user accounts that malicious actors compromise have special privileges they will exploit it. Depending on their intent, malicious actors may also destroy data (including backups).`
  },
  [MATURITY_LEVELS.ML2]: {
    title: "Maturity Level Two",
    description: `The focus of this maturity level is malicious actors operating with a modest step-up in capability from the previous maturity level. These malicious actors are willing to invest more time in a target and, perhaps more importantly, in the effectiveness of their tools. For example, these malicious actors will likely employ well-known tradecraft in order to better attempt to bypass controls implemented by a target and evade detection. This includes actively targeting credentials using phishing and employing technical and social engineering techniques to circumvent weaker methods of multi-factor authentication.

Generally, malicious actors are likely to be more selective in their targeting but still somewhat conservative in the time, money and effort they may invest in a target. Malicious actors will likely invest time to ensure their phishing is effective and employ common social engineering techniques to trick users into weakening the security of a system and launch malicious applications. If user accounts that malicious actors compromise have special privileges they will exploit it, otherwise they will seek user accounts with special privileges. Depending on their intent, malicious actors may also destroy all data (including backups) accessible to a user account with special privileges.

The guidance below outlines the requirements to be assessed in addition to the requirements of the previous maturity level. In doing so, assessments against Maturity Level Two should focus on the delta between Maturity Level One and Maturity Level Two.`
  },
  [MATURITY_LEVELS.ML3]: {
    title: "Maturity Level Three",
    description: `The focus of this maturity level is malicious actors who are more adaptive and much less reliant on public tools and techniques. These malicious actors are able to exploit the opportunities provided by weaknesses in their target's cybersecurity posture, such as the existence of older software or inadequate logging and monitoring. Malicious actors do this to not only extend their access once initial access has been gained to a target, but to evade detection and solidify their presence. Malicious actors make swift use of exploits when they become publicly available as well as other tradecraft that can improve their chance of success.

Generally, malicious actors may be more focused on particular targets and, more importantly, are willing and able to invest some effort into circumventing the idiosyncrasies and particular policy and technical controls implemented by their targets. For example, this includes socially engineering a user to not only open a malicious document but also to unknowingly assist in bypassing controls. This can also include circumventing stronger multi-factor authentication by stealing authentication token values to impersonate a user. Once a foothold is gained on a system, malicious actors will seek to gain privileged credentials or password hashes, pivot to other parts of a network, and cover their tracks. Depending on their intent, malicious actors may also destroy all data (including backups).

The guidance below outlines the requirements to be assessed in addition to the requirements of the previous maturity level. In doing so, assessments against Maturity Level Three should focus on the delta between Maturity Level Two and Maturity Level Three.`
  }
};

// Component for displaying maturity level info in a modal
const MaturityLevelInfoModal = ({ maturityLevel, onClose }) => {
  const info = MATURITY_LEVEL_INFO[maturityLevel];
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{info.title}</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div className="prose max-w-none">
            {info.description.split('\n\n').map((paragraph, i) => (
              <p key={i} className="mb-4 text-gray-700">{paragraph}</p>
            ))}
          </div>
        </div>
        <div className="bg-gray-100 px-6 py-3 flex justify-end">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const E8ReferenceLayout = () => {
  // Get current user for methodology service
  const { user } = useAuth();
  
  // State for search, filters, and current view
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [filterMaturityLevel, setFilterMaturityLevel] = useState('all');
  const [filterControlType, setFilterControlType] = useState('all');
  const [filterStrategy, setFilterStrategy] = useState('all');
  const [implementations, setImplementations] = useState({});
  
  // State for methodology data
  const [methodologies, setMethodologies] = useState(new Map());
  const [loadingMethodologies, setLoadingMethodologies] = useState(true);
  
  // Load all methodologies on component mount
  useEffect(() => {
    const loadAllMethodologies = async () => {
      const allControlIds = [];
      
      // Collect all control IDs from questions
      Object.entries(questions).forEach(([controlTypeName, levels]) => {
        Object.entries(levels).forEach(([level, levelQuestions]) => {
          levelQuestions.forEach(question => {
            allControlIds.push(question.id);
          });
        });
      });
      
      const methodologyMap = new Map();
      
      // Load methodologies in parallel
      const loadPromises = allControlIds.map(async (controlId) => {
        try {
          const methodologyData = await methodologyService.getMethodology(controlId, user?.id);
          methodologyMap.set(controlId, methodologyData.methodology || 'No methodology available');
        } catch (error) {
          console.error(`Failed to load methodology for ${controlId}:`, error);
          methodologyMap.set(controlId, 'Failed to load methodology');
        }
      });
      
      await Promise.all(loadPromises);
      setMethodologies(methodologyMap);
      setLoadingMethodologies(false);
    };
    
    loadAllMethodologies();
  }, [user?.id]);
  
  // Helper function to get methodology text
  const getMethodologyText = (controlId) => {
    return methodologies.get(controlId) || 'Loading methodology...';
  };
  
  // Controls data transformed for rendering
  const controlsData = Object.entries(CONTROL_TYPES).map(([key, value]) => ({
    id: key,
    name: value,
    strategy: getControlStrategy(key),
    controls: {}
  }));
  
  // Populate controls data
  controlsData.forEach(controlType => {
    const controlQuestions = questions[controlType.name];
    if (controlQuestions) {
      Object.entries(controlQuestions).forEach(([level, levelQuestions]) => {
        if (!controlType.controls[level]) {
          controlType.controls[level] = [];
        }
        
        levelQuestions.forEach(question => {
          const controlId = question.id;
          controlType.controls[level].push({
            id: controlId,
            question: question.question,
            methodology: getMethodologyText(controlId),
            implementation: implementations[controlId] || '' // Load from state
          });
        });
      });
    }
  });
  
  // Group controls by strategy
  const controlsByStrategy = Object.entries(CONTROL_STRATEGIES).map(([strategyKey, strategy]) => ({
    id: strategyKey,
    title: strategy.title,
    description: strategy.description,
    controls: controlsData.filter(control => strategy.controls.includes(control.id))
  }));
  
  // Handle implementation changes
  const handleImplementationChange = (controlId, value) => {
    const newImplementations = {
      ...implementations,
      [controlId]: value
    };
    setImplementations(newImplementations);
  };
  
  // Filter controls based on search and filters
  const filteredStrategies = controlsByStrategy.map(strategy => {
    const filteredControls = strategy.controls.filter(controlType => {
      // Control type filter
      if (filterControlType !== 'all' && controlType.id !== filterControlType) {
        return false;
      }
      
      // Strategy filter
      if (filterStrategy !== 'all' && strategy.id !== filterStrategy) {
        return false;
      }
      
      // Maturity level filter
      if (filterMaturityLevel !== 'all') {
        const hasMatchingLevel = Object.keys(controlType.controls).includes(filterMaturityLevel);
        if (!hasMatchingLevel) {
          return false;
        }
      }
      
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesName = controlType.name.toLowerCase().includes(searchLower);
        const matchesControls = Object.values(controlType.controls).flat().some(
          control => control.question.toLowerCase().includes(searchLower) || 
                    control.id.toLowerCase().includes(searchLower)
        );
        
        return matchesName || matchesControls;
      }
      
      return true;
    });
    
    return {
      ...strategy,
      controls: filteredControls
    };
  }).filter(strategy => strategy.controls.length > 0);
  
  // Toggle expand/collapse of sections
  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  const toggleExpandAll = () => {
    if (Object.keys(expandedItems).length === 0) {
      // Expand all
      const newExpandedItems = {};
      controlsByStrategy.forEach(strategy => {
        newExpandedItems[strategy.id] = true;
        strategy.controls.forEach(controlType => {
          newExpandedItems[controlType.id] = true;
          Object.keys(controlType.controls).forEach(level => {
            newExpandedItems[`${controlType.id}-${level}`] = true;
            
            // Also expand each individual control
            controlType.controls[level].forEach(control => {
              newExpandedItems[`control-${control.id}`] = true;
            });
          });
        });
      });
      setExpandedItems(newExpandedItems);
    } else {
      // Collapse all
      setExpandedItems({});
    }
  };
  
  // Export functionality
  const exportData = () => {
    const exportObj = {
      implementations,
      strategies: filteredStrategies.map(strategy => ({
        id: strategy.id,
        title: strategy.title,
        description: strategy.description,
        controls: strategy.controls.map(controlType => ({
          id: controlType.id,
          name: controlType.name,
          controls: Object.entries(controlType.controls).map(([level, controls]) => ({
            level,
            items: controls.map(control => ({
              id: control.id,
              question: control.question,
              methodology: control.methodology,
              implementation: control.implementation
            }))
          }))
        }))
      }))
    };
    
    const dataStr = JSON.stringify(exportObj, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
    
    const exportFileDefaultName = 'e8_reference_export.json';
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };
  
  // Save all implementations
  const saveAllImplementations = () => {
    alert('Implementation details are available for this session only. Export them before signing out.');
  };
  
  // Add state for active maturity level info modal
  const [activeMlInfo, setActiveMlInfo] = useState(null);
  
  // Add state for active strategy info modal
  const [activeStrategyInfo, setActiveStrategyInfo] = useState(null);
  
  // Add state for strategy sidebar
  const [activeStrategySidebar, setActiveStrategySidebar] = useState(null);
  
  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-gray-200">
        <h1 className="text-2xl font-bold mb-2">Essential Eight (E8) Reference Tool</h1>
        <p className="text-gray-600 mb-4">Comprehensive reference for Essential Eight controls, test methodologies, and implementation guidance</p>

        <MaturityLevelsPanel 
          maturityLevelInfo={MATURITY_LEVEL_INFO}
          onSelectLevel={setActiveMlInfo}
        />
        
        {/* Search and Filter Bar */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center w-1/3">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Enter exact match phrase..."
                className="w-full pl-10 pr-4 py-2 border rounded-md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 text-gray-400 w-5 h-5" />
            </div>
            <button className="ml-2 bg-blue-600 text-white px-4 py-2 rounded-md">
              <Search className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center">
            <div className="mr-4">
              <select 
                className="px-4 py-2 border rounded-md"
                value={filterStrategy}
                onChange={(e) => setFilterStrategy(e.target.value)}
              >
                <option value="all">All Strategies</option>
                {Object.entries(CONTROL_STRATEGIES).map(([key, strategy]) => (
                  <option key={key} value={key}>{strategy.title}</option>
                ))}
              </select>
            </div>
            
            <div className="mr-4">
              <select 
                className="px-4 py-2 border rounded-md"
                value={filterMaturityLevel}
                onChange={(e) => setFilterMaturityLevel(e.target.value)}
              >
                <option value="all">All Maturity Levels</option>
                <option value={MATURITY_LEVELS.ML1}>Maturity Level 1</option>
                <option value={MATURITY_LEVELS.ML2}>Maturity Level 2</option>
                <option value={MATURITY_LEVELS.ML3}>Maturity Level 3</option>
              </select>
            </div>
            
            <div className="mr-4">
              <select 
                className="px-4 py-2 border rounded-md"
                value={filterControlType}
                onChange={(e) => setFilterControlType(e.target.value)}
              >
                <option value="all">All Control Types</option>
                {Object.entries(CONTROL_TYPES).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
            </div>
            
            <button 
              className="bg-blue-600 text-white px-4 py-2 rounded-md mr-2"
              onClick={() => {
                setFilterMaturityLevel('all');
                setFilterControlType('all');
                setFilterStrategy('all');
                setSearchQuery('');
              }}
            >
              <Filter className="w-5 h-5" />
            </button>
            
            <button 
              className="bg-green-600 text-white px-4 py-2 rounded-md mr-2"
              onClick={saveAllImplementations}
            >
              <span className="flex items-center">
                <Save className="w-5 h-5 mr-2" /> Save
              </span>
            </button>
            
            <button 
              className="bg-blue-600 text-white px-4 py-2 rounded-md"
              onClick={exportData}
            >
              <span className="flex items-center">
                <Download className="w-5 h-5 mr-2" /> Export
              </span>
            </button>
          </div>
        </div>
        
        <button 
          className="text-blue-600 hover:underline"
          onClick={toggleExpandAll}
        >
          {Object.keys(expandedItems).length === 0 ? 'Expand All' : 'Collapse All'}
        </button>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 overflow-auto p-6">
        {filteredStrategies.length === 0 ? (
          <div className="text-center p-8 text-gray-500">
            No controls match your search criteria.
          </div>
        ) : (
          <div className="space-y-8">
            {filteredStrategies.map(strategy => (
              <div key={strategy.id} className="border rounded-md overflow-hidden">
                {/* Strategy Header */}
                <div 
                  className="bg-indigo-100 p-4 cursor-pointer"
                  onClick={() => toggleExpand(strategy.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">{strategy.title}</h2>
                      <p className="text-sm text-gray-600 mt-1">{strategy.description}</p>
                    </div>
                    <div>
                      {expandedItems[strategy.id] ? 
                        <ChevronDown className="w-5 h-5 text-gray-600" /> : 
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                      }
                    </div>
                  </div>
                </div>
                
                {/* Strategy Content */}
                {expandedItems[strategy.id] && (
                  <div className="p-4 space-y-6">
                    {strategy.controls.map(controlType => (
                      <div 
                        key={controlType.id} 
                        className="border rounded-md overflow-hidden ml-4"
                      >
                        {/* Control Type Header */}
                        <div 
                          className={`p-4 cursor-pointer ${
                            controlType.id === 'APP_CONTROL' || controlType.id === 'OFFICE_MACROS' ? 'bg-yellow-100' : 'bg-yellow-100'
                          }`}
                          onClick={() => toggleExpand(controlType.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                {controlType.id === 'APP_CONTROL' ? (
                                  <div className="w-8 h-8 flex items-center justify-center bg-yellow-500 text-white rounded-md mr-3">
                                    <Shield className="w-5 h-5" />
                                  </div>
                                ) : controlType.id === 'OFFICE_MACROS' ? (
                                  <div className="w-8 h-8 flex items-center justify-center bg-yellow-500 text-white rounded-md mr-3">
                                    <FileText className="w-5 h-5" />
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 flex items-center justify-center bg-yellow-500 text-white rounded-md mr-3">
                                    {controlType.name.charAt(0)}
                                  </div>
                                )}
                                <h3 className="font-bold">{controlType.name}</h3>
                                
                                {/* Keep info button only for non-APP_CONTROL and non-OFFICE_MACROS strategies */}
                                {controlType.id !== 'APP_CONTROL' && controlType.id !== 'OFFICE_MACROS' && strategyInfoData[controlType.id] && (
                                  <button 
                                    className="ml-2 text-gray-500 hover:text-gray-700"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveStrategyInfo(strategyInfoData[controlType.id]);
                                    }}
                                  >
                                    <Info className="w-4 h-4" />
                                  </button>
                                )}
                                
                                {/* Add info button for OFFICE_MACROS */}
                                {controlType.id === 'OFFICE_MACROS' && strategyInfoData[controlType.id] && (
                                  <button 
                                    className="ml-2 text-gray-500 hover:text-gray-700"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveStrategyInfo(strategyInfoData[controlType.id]);
                                    }}
                                  >
                                    <Info className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                              
                              {/* Add description for APP_CONTROL */}
                              {controlType.id === 'APP_CONTROL' && (
                                <div className="mt-2 ml-11 bg-white bg-opacity-60 p-3 rounded-md border-l-4 border-yellow-400">
                                  <p className="text-sm text-gray-700 leading-relaxed">
                                    Application control is a security measure that ensures only approved applications and scripts can run on systems. 
                                    By blocking unauthorised or unknown software, it helps prevent the execution of malware and reduces the risk of 
                                    compromise through user error or exploitation.
                                  </p>
                                </div>
                              )}
                              
                              {/* Add description for OFFICE_MACROS */}
                              {controlType.id === 'OFFICE_MACROS' && (
                                <div className="mt-2 ml-11 bg-white bg-opacity-60 p-3 rounded-md border-l-4 border-yellow-400">
                                  <p className="text-sm text-gray-700 leading-relaxed">
                                    This strategy aims to reduce the risk of malicious code execution by managing how Microsoft Office macros are used. 
                                    By restricting or disabling macros, particularly those from untrusted sources, organisations can prevent attackers 
                                    from exploiting macro-based malware to compromise systems.
                                  </p>
                                </div>
                              )}
                            </div>
                            <div>
                              {expandedItems[controlType.id] ? 
                                <ChevronDown className="w-5 h-5 text-gray-600" /> : 
                                <ChevronRight className="w-5 h-5 text-gray-600" />
                              }
                            </div>
                          </div>
                        </div>
                        
                        {/* Control Type Content */}
                        {expandedItems[controlType.id] && (
                          <div className="p-4">
                            {/* Maturity Levels */}
                            {Object.entries(controlType.controls)
                              .filter(([level]) => filterMaturityLevel === 'all' || level === filterMaturityLevel)
                              .map(([level, controls]) => (
                                <div key={level} className="mt-4">
                                  {/* Maturity Level Header */}
                                  <div 
                                    className={`p-3 cursor-pointer ${
                                      level === MATURITY_LEVELS.ML1 ? 'bg-blue-100' :
                                      level === MATURITY_LEVELS.ML2 ? 'bg-purple-100' : 'bg-green-100'
                                    }`}
                                    onClick={() => toggleExpand(`${controlType.id}-${level}`)}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center">
                                        <div className={`w-6 h-6 flex items-center justify-center rounded-md mr-3 ${
                                          level === MATURITY_LEVELS.ML1 ? 'bg-blue-500' :
                                          level === MATURITY_LEVELS.ML2 ? 'bg-purple-500' : 'bg-green-500'
                                        } text-white`}>
                                          {level === MATURITY_LEVELS.ML1 ? '1' : 
                                          level === MATURITY_LEVELS.ML2 ? '2' : '3'}
                                        </div>
                                        <h3 className="font-medium">Maturity Level {level.substring(2)}</h3>
                                        
                                        {/* Enhanced info button for Maturity Levels */}
                                        <button 
                                          className={`ml-3 flex items-center justify-center px-2 py-1 rounded-md transition-colors duration-150 ${
                                            level === MATURITY_LEVELS.ML1 ? 'bg-blue-200 hover:bg-blue-300 text-blue-700' :
                                            level === MATURITY_LEVELS.ML2 ? 'bg-purple-200 hover:bg-purple-300 text-purple-700' :
                                            'bg-green-200 hover:bg-green-300 text-green-700'
                                          }`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveStrategySidebar({
                                              controlId: controlType.id,
                                              level: level
                                            });
                                          }}
                                          title="View detailed maturity level information"
                                        >
                                          <Info className="w-4 h-4 mr-1" />
                                          <span className="text-xs font-medium">View Info</span>
                                        </button>
                                      </div>
                                      <div>
                                        {expandedItems[`${controlType.id}-${level}`] ? 
                                          <ChevronDown className="w-5 h-5 text-gray-600" /> : 
                                          <ChevronRight className="w-5 h-5 text-gray-600" />
                                        }
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Control Items */}
                                  {expandedItems[`${controlType.id}-${level}`] && (
                                    <div className="bg-gray-50 p-4 mt-2 space-y-4">
                                      {controls.map(control => {
                                        const controlExpandId = `control-${control.id}`;
                                        const isControlExpanded = expandedItems[controlExpandId];
                                        
                                        return (
                                          <div key={control.id} className="border border-gray-200 rounded-md overflow-hidden mb-4 last:mb-0">
                                            {/* Control Header - Clickable to expand/collapse */}
                                            <div 
                                              className="bg-blue-50 p-3 cursor-pointer flex items-center justify-between"
                                              onClick={() => toggleExpand(controlExpandId)}
                                            >
                                              <div className="flex items-center">
                                                <div className="flex-shrink-0 mr-2">
                                                  <input type="checkbox" className="w-4 h-4" disabled checked={false} />
                                                </div>
                                                <div>
                                                  <div className="font-medium text-blue-600">{control.id}</div>
                                                  <div className="font-medium text-gray-800">{control.question}</div>
                                                </div>
                                              </div>
                                              <div>
                                                {isControlExpanded ? 
                                                  <ChevronUp className="w-5 h-5 text-gray-600" /> : 
                                                  <ChevronDown className="w-5 h-5 text-gray-600" />
                                                }
                                              </div>
                                            </div>
                                            
                                            {/* Control Content - Only visible when expanded */}
                                            {isControlExpanded && (
                                              <div className="p-4">
                                                {/* Implementation Examples */}
                                                <div className="mb-4">
                                                  <h4 className="text-sm font-medium mb-2">Implementation Examples</h4>
                                                  <div className="ml-4 text-sm text-gray-700 space-y-1">
                                                    <div>Ex1: Begin procedures during incident response</div>
                                                    <div>Ex2: Make individuals aware of recovery plans</div>
                                                  </div>
                                                </div>
                                                
                                                {/* Test Methodology */}
                                                <div className="mb-4">
                                                  <h4 className="text-sm font-medium mb-2">Test Methodology</h4>
                                                  <div className="ml-4 text-sm text-gray-700 whitespace-pre-line">
                                                    {loadingMethodologies ? (
                                                      <div className="flex items-center text-gray-500">
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Loading methodology...
                                                      </div>
                                                    ) : (
                                                      control.methodology || 'No test methodology provided.'
                                                    )}
                                                  </div>
                                                </div>
                                                
                                                {/* Implementation - Editable */}
                                                <div>
                                                  <h4 className="text-sm font-medium mb-2">Implementation</h4>
                                                  <div className="ml-4">
                                                    <textarea
                                                      className="w-full p-3 border border-gray-300 rounded-md text-sm text-gray-700"
                                                      placeholder="Enter implementation details..."
                                                      rows={4}
                                                      value={control.implementation}
                                                      onChange={(e) => handleImplementationChange(control.id, e.target.value)}
                                                    />
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Maturity Level Info Modal */}
      {activeMlInfo && (
        <MaturityLevelInfoModal 
          maturityLevel={activeMlInfo} 
          onClose={() => setActiveMlInfo(null)} 
        />
      )}
      
      {/* Strategy Info Modal */}
      {activeStrategyInfo && (
        <StrategyInfoModal 
          strategyInfo={activeStrategyInfo} 
          onClose={() => setActiveStrategyInfo(null)} 
        />
      )}
      
      <SidebarOverlay 
        isVisible={!!activeStrategySidebar} 
        onClose={() => setActiveStrategySidebar(null)} 
      />
      
      {activeStrategySidebar && (
        <StrategySidebar
          strategyInfo={strategyInfoData[activeStrategySidebar.controlId]}
          maturityLevel={activeStrategySidebar.level}
          onClose={() => setActiveStrategySidebar(null)}
        />
      )}
    </div>
  );
};

export default E8ReferenceLayout;
